/**
 * Waadoo design tokens — the one palette both style systems of the app read.
 *
 * Backstage UI (BUI, `@backstage/ui`) reads them as CSS custom properties,
 * declared in `packages/app/src/styles.css`; the Material UI theme of
 * `packages/app/src/themes.ts` — which still styles every upstream page we do
 * not rewrite (catalog, search, techdocs, scaffolder, settings) and the
 * sidebar — reads them from here.
 *
 * KEEP BOTH IN SYNC. `styles.css` holds the same values as literals, keyed by
 * the same custom-property names, and `tokens.test.ts` fails as soon as one
 * side changes without the other.
 *
 * Dans le cœur (`components/ui`) depuis l'étape 9c du plan « plateforme
 * d'applications » : `icons/appLogos.ts` y lit les couleurs des cartes, et
 * l'application (`themes.ts`, la barre latérale) les importe d'ici.
 *
 * Brand: the two colours of the logo, orange #f15a24 and black #000000. White
 * on #f15a24 is only 3.37:1, too little for body text, so the brand orange is
 * kept for large or non-text marks (logo, active indicators) and
 * the interactive accent is a darker orange in light mode (#c2410c, 5.18:1
 * under white text) and a lighter one in dark mode (#ff7a45, under dark text).
 * The contrast of every text/background pair is recorded in CUSTOMIZATIONS.md
 * (entry of 2026-10-01).
 */

/** The two colours measured on the logo. */
export const brand = {
  orange: '#f15a24',
  black: '#000000',
};

/**
 * Inter, self-hosted (`packages/app/src/fonts.css`, @fontsource-variable/inter), then the
 * system stack while it loads (`font-display: swap`) or if it cannot.
 */
export const fontFamily =
  "'Inter Variable', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const monospaceFontFamily =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

/** Radii in px, for the Material UI side. The BUI side is `--bui-radius-*`. */
export const radius = {
  /** Small marks: tags inside dense tables, skeletons. */
  small: 6,
  /** Controls: inputs, menus, tooltips. */
  control: 8,
  /**
   * Action buttons (« P3 », 2026-10-18): BUI's Button, ButtonLink and
   * ButtonIcon (`--waadoo-radius-button`, styles.css) and Material UI's
   * Button.
   */
  button: 10,
  /** Surfaces: cards, papers, popovers. */
  card: 12,
  /** Dialogs. */
  dialog: 16,
  /** Pills: chips, the active sidebar item. */
  pill: 999,
};

/**
 * Where the pill of a sidebar item (the hovered or active one, themes.ts
 * `BackstageSidebarItem`) sits inside its row, in px: `block` from the top
 * and bottom, `inline` from the left and right. The user block above the
 * menu (modules/nav/SidebarUserBlock.tsx) lines its power button and theme
 * switch up on the same edges.
 */
export const sidebarPillInset = { block: 4, inline: 8 };

/** Tokens identical in both modes. Mirrored in `styles.css`. */
export const sharedTokens = {
  '--bui-font-regular': fontFamily,
  '--bui-font-monospace': monospaceFontFamily,
  // BUI reads radius-2 for buttons, inputs, menus, tags and radius-3 for
  // cards, popovers, alerts (@backstage/ui 0.18.0 component styles).
  '--bui-radius-1': `${radius.small}px`,
  '--bui-radius-2': `${radius.control}px`,
  '--bui-radius-3': `${radius.card}px`,
  '--bui-radius-4': `${radius.dialog}px`,
  '--bui-radius-5': '20px',
  '--bui-radius-6': '24px',
  '--bui-radius-full': `${radius.pill}px`,
  // Not a BUI token: the action buttons' radius (« P3 », styles.css). BUI
  // reads radius-2 for its buttons, which the inputs and menus share.
  '--waadoo-radius-button': `${radius.button}px`,
  // Not a BUI token: BUI has no brand slot, only an interactive one.
  '--waadoo-brand': brand.orange,
  // The app's type scale for secondary text (pills, tags, counts, notes):
  // 12px, BUI's font-size-2. Not font-size-1, which is 10px (0.625rem).
  '--waadoo-font-size-secondary': 'var(--bui-font-size-2)',
  // Uppercase letter-spaced table headers only: 11px.
  '--waadoo-font-size-label': '0.6875rem',
};

/** Light mode. Mirrored in `styles.css`. */
export const lightTokens = {
  // Surfaces, warm greys. neutral-N is the surface of nesting level N (the
  // app background is level 0); -hover and -pressed step one and two levels
  // darker, so a hovered surface always differs from its parent.
  '--bui-bg-app': '#f6f6f4',
  '--bui-bg-neutral-1': '#ffffff',
  '--bui-bg-neutral-1-hover': '#f1f0ed',
  '--bui-bg-neutral-1-pressed': '#e9e7e3',
  '--bui-bg-neutral-1-disabled': '#f1f0ed',
  '--bui-bg-neutral-2': '#f1f0ed',
  '--bui-bg-neutral-2-hover': '#e9e7e3',
  '--bui-bg-neutral-2-pressed': '#e1dfda',
  '--bui-bg-neutral-2-disabled': '#f1f0ed',
  '--bui-bg-neutral-3': '#e9e7e3',
  '--bui-bg-neutral-3-hover': '#e1dfda',
  '--bui-bg-neutral-3-pressed': '#d8d5cf',
  '--bui-bg-neutral-3-disabled': '#e9e7e3',
  '--bui-bg-neutral-4': '#e1dfda',
  '--bui-bg-neutral-4-hover': '#d8d5cf',
  '--bui-bg-neutral-4-pressed': '#cfccc5',
  '--bui-bg-neutral-4-disabled': '#e1dfda',

  // Interactive accent (primary buttons, focus ring, links).
  '--bui-bg-solid': '#c2410c',
  '--bui-bg-solid-hover': '#9a3412',
  '--bui-bg-solid-pressed': '#7c2d12',
  '--bui-bg-solid-disabled': '#f3dfd5',
  '--bui-fg-solid': '#ffffff',
  '--bui-fg-solid-disabled': '#8a5a45',

  // Ink.
  '--bui-fg-primary': '#111418',
  '--bui-fg-secondary': '#5b6470',
  '--bui-fg-disabled': '#a3a9b1',

  // States. Warning is amber, never orange, so it cannot be read as the brand.
  '--bui-bg-success': '#e7f6ec',
  '--bui-bg-warning': '#fdf3d1',
  '--bui-bg-danger': '#fdecec',
  '--bui-bg-info': '#e8effd',
  '--bui-fg-success': '#15733a',
  '--bui-fg-warning': '#8a5b00',
  '--bui-fg-danger': '#b91c1c',
  '--bui-fg-info': '#1d4ed8',
  '--bui-fg-success-on-bg': '#166534',
  '--bui-fg-warning-on-bg': '#854d0e',
  '--bui-fg-danger-on-bg': '#991b1b',
  '--bui-fg-info-on-bg': '#1e40af',

  // Lines.
  '--bui-border-1': '#e6e4df',
  '--bui-border-2': '#d9d6cf',
  '--bui-border-success': '#86d19f',
  '--bui-border-warning': '#e8c25a',
  '--bui-border-danger': '#f0a3a3',
  '--bui-border-info': '#9dbbf2',
  // The outline of the controls at rest (styles.css): the folder tabs' line,
  // border-2 (« B1 », 2026-10-18), 1.45:1 on a card — under the 3:1 of WCAG
  // 1.4.11 for a control's boundary, a deliberate choice.
  '--waadoo-border-control': 'var(--bui-border-2)',
  // A line at 3:1 (3.97:1 on a card, 3.67:1 on the page): the outline of a
  // hovered or focused control.
  '--waadoo-border-strong': '#857f76',

  // Special.
  '--bui-ring': '#c2410c',
  '--bui-scrollbar': '#b8b4ac3b',
  '--bui-scrollbar-thumb': '#b8b4ac',

  // Elevation: BUI's single shadow is the one of overlays (menus, popovers,
  // tooltips); cards get the lighter one below.
  '--bui-shadow':
    '0 1px 2px rgb(17 20 24 / 6%), 0 8px 24px -4px rgb(17 20 24 / 12%)',
  '--waadoo-shadow-card':
    '0 1px 2px rgb(17 20 24 / 4%), 0 2px 6px -2px rgb(17 20 24 / 6%)',

  // Text links. BUI's own Link is ink-coloured; ours take the accent.
  '--waadoo-link': '#c2410c',
  // The square behind the glyph of a « create » slot (CreateButton.tsx): the
  // accent at 14 % on the card (neutral-1).
  '--waadoo-accent-tint': '#f6e4dd',
  '--waadoo-odoo': '#714b67',
  // The home page's project cards (ProjectCard.tsx): the tile behind the
  // Odoo logo (the purple at 14 % on the card) and the title's ink, one step
  // softer than --bui-fg-primary so the title does not shout.
  '--waadoo-odoo-tint': '#ebe6ea',
  // The same tile, and the glyph of the new branch dialog's « what will be
  // created » line, for a type without colour of its own (every type but
  // Odoo, `[data-app-type]` in styles.css): a neutral ink, the tint its 14 %
  // on the card (genericity plan, step 7).
  '--waadoo-app': '#5b6472',
  '--waadoo-app-tint': '#e8e9eb',
  '--waadoo-title-soft': '#3a3f47',
  // The elephant of the PostgreSQL version badge (PodPlacementBits.tsx):
  // PostgreSQL's own blue. 3:1 at least on the card and the badge.
  '--waadoo-postgres': '#336791',
  // The body of an ephemeral branch's ghost (ghostIcon.tsx; its fork is in
  // the ephemeral tier's violet). A graphic, 3:1 at least wherever it sits.
  '--waadoo-ephemeral': '#3b5bdb',
  '--waadoo-link-hover': '#9a3412',
  '--waadoo-wordmark': '#3a3f45',

  // The tinted badges of the pods card (PodPlacementBits.tsx, styles.css
  // `.waadoo-pod-badges`): a light background, the text and glyph in a dark
  // shade of the same hue. Blue, violet, teal: the zones a, b, c; cyan: the
  // « HPA » badge. The other tints of that line are the state pairs above
  // (green: success, amber: warning) and the neutral one (neutral-2,
  // fg-secondary). Each fg on its bg ≥ 4.5:1: components/ui/tokens.test.ts.
  '--waadoo-tint-blue-bg': '#e8effd',
  '--waadoo-tint-blue-fg': '#1e40af',
  '--waadoo-tint-violet-bg': '#efeafd',
  '--waadoo-tint-violet-fg': '#5b21b6',
  '--waadoo-tint-teal-bg': '#def5f1',
  '--waadoo-tint-teal-fg': '#115e59',
  '--waadoo-tint-cyan-bg': '#ddf3f9',
  '--waadoo-tint-cyan-fg': '#155e75',

  // The tiers of the branches (`branchTier`, branchKind.ts; palette « T3
  // sobre », the ephemeral violet of proposal A, 2026-10-29): the bare
  // colour is a graphic (the tier dot, the edge of a row, the outline of a
  // sleeping environment tag), 3:1 at least; the ink is the environment
  // tag's text (`EnvironmentTag`, BranchLabel.tsx) on its tint and on the
  // surfaces, 4.5:1 at least.
  '--waadoo-tier-production': '#d64545',
  '--waadoo-tier-production-ink': '#a82f2f',
  '--waadoo-tier-production-tint': '#fbeaea',
  '--waadoo-tier-staging': '#3b7dd8',
  '--waadoo-tier-staging-ink': '#2559a8',
  '--waadoo-tier-staging-tint': '#e8f0fb',
  '--waadoo-tier-development': '#6b7280',
  '--waadoo-tier-development-ink': '#4b5260',
  '--waadoo-tier-development-tint': '#eef0f2',
  '--waadoo-tier-ephemeral': '#8b5cf6',
  '--waadoo-tier-ephemeral-ink': '#6d28d9',
  '--waadoo-tier-ephemeral-tint': '#f1ebfe',
};

/** Same keys as the light set, enforced by the type. */
export type ModeTokens = typeof lightTokens;

/** Dark mode. Mirrored in `styles.css`. */
export const darkTokens: ModeTokens = {
  '--bui-bg-app': '#111418',
  '--bui-bg-neutral-1': '#181c22',
  '--bui-bg-neutral-1-hover': '#20252d',
  '--bui-bg-neutral-1-pressed': '#282e37',
  '--bui-bg-neutral-1-disabled': '#20252d',
  '--bui-bg-neutral-2': '#20252d',
  '--bui-bg-neutral-2-hover': '#282e37',
  '--bui-bg-neutral-2-pressed': '#313842',
  '--bui-bg-neutral-2-disabled': '#20252d',
  '--bui-bg-neutral-3': '#282e37',
  '--bui-bg-neutral-3-hover': '#313842',
  '--bui-bg-neutral-3-pressed': '#3a424d',
  '--bui-bg-neutral-3-disabled': '#282e37',
  '--bui-bg-neutral-4': '#313842',
  '--bui-bg-neutral-4-hover': '#3a424d',
  '--bui-bg-neutral-4-pressed': '#444c58',
  '--bui-bg-neutral-4-disabled': '#313842',

  // A light accent under dark text; hover and pressed get lighter, which
  // raises the contrast instead of lowering it.
  '--bui-bg-solid': '#ff7a45',
  '--bui-bg-solid-hover': '#ff9160',
  '--bui-bg-solid-pressed': '#ffa47c',
  '--bui-bg-solid-disabled': '#3d2a22',
  '--bui-fg-solid': '#111418',
  '--bui-fg-solid-disabled': '#b08c7c',

  '--bui-fg-primary': '#e8eaed',
  '--bui-fg-secondary': '#9aa3ae',
  '--bui-fg-disabled': '#5f6773',

  '--bui-bg-success': '#0f2a1b',
  '--bui-bg-warning': '#2e2408',
  '--bui-bg-danger': '#3a1414',
  '--bui-bg-info': '#122044',
  '--bui-fg-success': '#4ade80',
  '--bui-fg-warning': '#fbbf24',
  '--bui-fg-danger': '#f87171',
  '--bui-fg-info': '#7ab0ff',
  '--bui-fg-success-on-bg': '#86efac',
  '--bui-fg-warning-on-bg': '#fcd34d',
  '--bui-fg-danger-on-bg': '#fca5a5',
  '--bui-fg-info-on-bg': '#a5c8ff',

  '--bui-border-1': '#2c323b',
  '--bui-border-2': '#3a414b',
  '--bui-border-success': '#2f7a4b',
  '--bui-border-warning': '#8a6a1c',
  '--bui-border-danger': '#8f3b3b',
  '--bui-border-info': '#3b5c9e',
  // border-2, as in light mode: 1.66:1 on a card.
  '--waadoo-border-control': 'var(--bui-border-2)',
  // 3.95:1 on a card, 4.26:1 on the page background.
  '--waadoo-border-strong': '#727a87',

  '--bui-ring': '#ff7a45',
  '--bui-scrollbar': '#3a414b3b',
  '--bui-scrollbar-thumb': '#4b535e',

  '--bui-shadow':
    '0 1px 2px rgb(0 0 0 / 30%), 0 8px 24px -4px rgb(0 0 0 / 45%)',
  '--waadoo-shadow-card': '0 1px 2px rgb(0 0 0 / 25%)',

  '--waadoo-link': '#ff7a45',
  '--waadoo-accent-tint': '#382927',
  '--waadoo-odoo': '#b98fb0',
  '--waadoo-odoo-tint': '#2f2c36',
  '--waadoo-app': '#9aa3af',
  '--waadoo-app-tint': '#2a2f36',
  '--waadoo-title-soft': '#d3d7dc',
  // #336791 lightened in its own hue: the original is 2.56:1 on neutral-2.
  '--waadoo-postgres': '#6b9bd1',
  '--waadoo-ephemeral': '#7b9bff',
  '--waadoo-link-hover': '#ff9160',
  '--waadoo-wordmark': '#c9ced4',

  // The tints in reverse: a dark background, a light shade of the hue.
  '--waadoo-tint-blue-bg': '#172a4f',
  '--waadoo-tint-blue-fg': '#a5c8ff',
  '--waadoo-tint-violet-bg': '#2a2150',
  '--waadoo-tint-violet-fg': '#c4b5fd',
  '--waadoo-tint-teal-bg': '#0f302d',
  '--waadoo-tint-teal-fg': '#5eead4',
  '--waadoo-tint-cyan-bg': '#0c2d38',
  '--waadoo-tint-cyan-fg': '#67e8f9',

  '--waadoo-tier-production': '#f2675b',
  '--waadoo-tier-production-ink': '#ff9a90',
  '--waadoo-tier-production-tint': '#3a1b1a',
  '--waadoo-tier-staging': '#60a5fa',
  '--waadoo-tier-staging-ink': '#9cc8ff',
  '--waadoo-tier-staging-tint': '#132640',
  '--waadoo-tier-development': '#a1a7b3',
  '--waadoo-tier-development-ink': '#c9ced6',
  '--waadoo-tier-development-tint': '#272c34',
  '--waadoo-tier-ephemeral': '#a78bfa',
  '--waadoo-tier-ephemeral-ink': '#c4b5fd',
  '--waadoo-tier-ephemeral-tint': '#261d45',
};

/**
 * Values only the Material UI theme needs: the sidebar is core-components,
 * with no BUI counterpart to share a custom property with. Not mirrored in
 * `styles.css`. (Page headers need none since 2026-10-01: they are drawn on
 * the app background with the ink tokens above.)
 */
export interface MuiOnlyTokens {
  /** Tint of the active sidebar pill: the brand orange over the sidebar. */
  navSelectedBackground: string;
}

export const lightMuiOnly: MuiOnlyTokens = {
  // 10 % of #f15a24 over #ffffff: #c2410c on it is still 4.59:1.
  navSelectedBackground: '#feeee9',
};

export const darkMuiOnly: MuiOnlyTokens = {
  // 16 % of #f15a24 over #181c22: #ff7a45 on it is 5.46:1.
  navSelectedBackground: '#3b2622',
};
